<div class="vendor-translation-shell">
  <app-breadcrumb [levels]="[{ label: 'New Quote Request' }]"></app-breadcrumb>
  @if (!vendorsRetrieved()) {
    <div class="vendor-loading" role="status" aria-live="polite">
      <span class="spinner" aria-hidden="true"></span>
      Loading...
    </div>
  }

  @if (vendorsRetrieved() && !hasVendorAccess()) {
    <div class="vendor-no-access" role="alert">
      <span class="material-symbols-outlined no-access-icon" aria-hidden="true"
        >block</span
      >
      <h3>No Suppliers Available</h3>
      <p>
        There are currently no vendors available to handle translation requests.
        Please try again later or contact support.
      </p>
    </div>
  }

  <div
    class="vendor-translation-content"
    [hidden]="!vendorsRetrieved() || !hasVendorAccess()"
  >
    <!-- ── Page Title ────────────────────────────────────────────────────── -->
    <h2 class="page-title">New Quote Request</h2>

    @if (showCancelledReason()) {
      <section
        class="vendor-cancelled-reason"
        data-testid="vendor-cancel-reason-display"
      >
        <h3 class="vendor-cancelled-reason__title">Cancellation Reason</h3>
        <p class="vendor-cancelled-reason__text">{{ cancelledReasonText() }}</p>
      </section>
    }

    <form [formGroup]="quoteForm" (ngSubmit)="onSubmit()" novalidate>
      <!-- ═══════════════════════════════════════════════════════════════════ -->
      <!-- Section 1: Request Overview                                       -->
      <!-- ═══════════════════════════════════════════════════════════════════ -->
      <section class="form-section" id="request-overview">
        <h3 class="section-title">Request Overview</h3>
        <p class="section-description">
          Core details used to uniquely identify each legal entity.
        </p>

        <!-- Row 1: Job Name, Content Type, Therapeutic Area -->
        <div class="form-grid three-col" id="request-overview">
          <div class="form-group">
            <label for="jobName" class="form-label">
              Job Name<span class="required" aria-hidden="true">*</span>
            </label>
            <textarea
              id="jobName"
              class="form-control"
              formControlName="jobName"
              maxlength="64"
              rows="2"
              [class.error]="isFieldInvalid('jobName')"
              aria-required="true"
              [attr.aria-invalid]="isFieldInvalid('jobName')"
              [attr.aria-describedby]="
                isFieldInvalid('jobName') ? 'jobName-error' : null
              "
            ></textarea>
            @if (
              isFieldInvalid("jobName") &&
              quoteForm.get("jobName")?.hasError("required")
            ) {
              <span class="field-error" id="jobName-error" role="alert"
                >Job Name is required.</span
              >
            }
            @if (
              isFieldInvalid("jobName") &&
              quoteForm.get("jobName")?.hasError("invalidCharacters")
            ) {
              <span class="field-error" id="jobName-error" role="alert"
                >Contains invalid characters (\\ / : * ? " , &lt; &gt; |
                {{ "{" }} {{ "}" }}).</span
              >
            }
            @if (
              isFieldInvalid("jobName") &&
              quoteForm.get("jobName")?.hasError("maxlength")
            ) {
              <span class="field-error" id="jobName-error" role="alert"
                >Maximum 64 characters allowed.</span
              >
            }
          </div>

          <div class="form-group">
            <label for="contentType" class="form-label">
              Content Type<span class="required" aria-hidden="true">*</span>
            </label>
            <select
              id="contentType"
              class="form-control form-select"
              formControlName="contentType"
              [class.error]="isFieldInvalid('contentType')"
              aria-required="true"
              [attr.aria-invalid]="isFieldInvalid('contentType')"
            >
              <option value="" disabled selected></option>
              @for (type of contentTypes; track type.value) {
                <option [value]="type.value">{{ type.name }}</option>
              }
            </select>
            @if (
              isFieldInvalid("contentType") &&
              quoteForm.get("contentType")?.hasError("required")
            ) {
              <span class="field-error" role="alert"
                >Content Type is required.</span
              >
            }
          </div>

          <div class="form-group">
            <label for="therapeuticArea" class="form-label">
              Therapeutic Area<span class="required" aria-hidden="true">*</span>
            </label>
            <select
              id="therapeuticArea"
              class="form-control form-select"
              formControlName="therapeuticArea"
              [class.error]="isFieldInvalid('therapeuticArea')"
              aria-required="true"
            >
              <option value="" disabled selected></option>
              @for (area of therapeuticAreas; track area.value) {
                <option [value]="area.value">{{ area.value }}</option>
              }
            </select>
            @if (
              isFieldInvalid("therapeuticArea") &&
              quoteForm.get("therapeuticArea")?.hasError("required")
            ) {
              <span class="field-error" role="alert"
                >Therapeutic Area is required.</span
              >
            }
          </div>
        </div>

        <!-- Row 2: Target Audience + Document Tone of Voice + conditional Product -->
        <div class="form-grid three-col request-overview">
          <div class="form-group">
            <label class="form-label" id="targetAudience-label">
              Target Audience<span class="required" aria-hidden="true">*</span>
            </label>
            <mat-radio-group
              id="targetAudience"
              formControlName="targetAudience"
              class="radio-group-vertical"
              [class.error]="isFieldInvalid('targetAudience')"
              aria-labelledby="targetAudience-label"
            >
              <mat-radio-button value="AstraZeneca Internal Use"
                >AstraZeneca Internal Use</mat-radio-button
              >
              <mat-radio-button value="General Public"
                >General Public</mat-radio-button
              >
              <mat-radio-button value="Healthcare Professionals"
                >Healthcare Professionals</mat-radio-button
              >
              <mat-radio-button value="Patients">Patients</mat-radio-button>
            </mat-radio-group>
            @if (
              isFieldInvalid("targetAudience") &&
              quoteForm.get("targetAudience")?.hasError("required")
            ) {
              <span class="field-error" role="alert"
                >Target Audience is required.</span
              >
            }
          </div>

          <div class="form-group">
            <label class="form-label" id="toneOfVoice-label">
              Document Tone of Voice<span class="required" aria-hidden="true"
                >*</span
              >
            </label>
            <mat-radio-group
              id="documentToneOfVoice"
              formControlName="documentToneOfVoice"
              class="radio-group-vertical"
              [class.error]="isFieldInvalid('documentToneOfVoice')"
              aria-labelledby="toneOfVoice-label"
            >
              <mat-radio-button value="Formal">Formal</mat-radio-button>
              <mat-radio-button value="Informal">Informal</mat-radio-button>
            </mat-radio-group>
            @if (
              isFieldInvalid("documentToneOfVoice") &&
              quoteForm.get("documentToneOfVoice")?.hasError("required")
            ) {
              <span class="field-error" role="alert"
                >Document Tone of Voice is required.</span
              >
            }
          </div>

          @if (showProductField()) {
            <div class="form-group">
              <label for="product" class="form-label">
                Product<span class="required" aria-hidden="true">*</span>
              </label>
              <select
                id="product"
                class="form-control form-select"
                formControlName="product"
                [class.error]="isFieldInvalid('product')"
                aria-required="true"
              >
                <option value="" disabled selected></option>
                @for (prod of availableProductsList(); track prod) {
                  <option [value]="prod">{{ prod }}</option>
                }
              </select>
              @if (
                isFieldInvalid("product") &&
                quoteForm.get("product")?.hasError("required")
              ) {
                <span class="field-error" role="alert"
                  >Product is required.</span
                >
              }
            </div>
          }
        </div>
      </section>

      <hr class="section-divider" />
      <section class="form-section" id="languages">
        <h3 class="section-title">Languages</h3>
        <p class="section-description">
          Core details used to uniquely identify each legal entity.
        </p>

        <div class="form-grid languages-wrapper">
          <div class="form-group source-dropdown-wrapper">
            <label for="translateFrom" class="form-label">
              Translate from<span class="required" aria-hidden="true">*</span>
            </label>
            <app-language-dropdown
              class="source-dropdown"
              [languages]="languages()"
              [value]="selectedSourceLanguage()"
              [singleSelection]="true"
              [filterBy]="''"
              (addLanguage)="onSourceLanguageChange($event)"
              (removeLanguage)="onSourceLanguageClear()"
              (blurred)="quoteForm.get('translateFrom')?.markAsTouched()"
              [class.error]="isFieldInvalid('translateFrom')"
            >
            </app-language-dropdown>
            @if (
              isFieldInvalid("translateFrom") &&
              quoteForm.get("translateFrom")?.hasError("required")
            ) {
              <span class="field-error" role="alert"
                >Source language is required.</span
              >
            }
          </div>

          <div class="form-group target-dropdown-wrapper">
            <label for="translateTo" class="form-label">
              Translate to<span class="required" aria-hidden="true">*</span>
            </label>
            <app-language-dropdown
              class="target-dropdown"
              [languages]="availableTargetLanguages()"
              [singleSelection]="false"
              [filterBy]="selectedSourceLanguage()"
              [selectedLanguages]="selectedTargetLanguages()"
              (addLanguage)="onTargetLanguageAdd($event)"
              (addLanguages)="onTargetLanguagesSelectAll($event)"
              (removeLanguage)="onTargetLanguageRemove($event)"
              (blurred)="quoteForm.get('translateTo')?.markAsTouched()"
              [class.error]="
                isFieldInvalid('translateTo') ||
                quoteForm.hasError('sameLanguage')
              "
            >
            </app-language-dropdown>
            @if (
              isFieldInvalid("translateTo") &&
              quoteForm.get("translateTo")?.hasError("required")
            ) {
              <span class="field-error" role="alert"
                >At least one target language is required.</span
              >
            }
            @if (quoteForm.hasError("sameLanguage")) {
              <span class="field-error" role="alert"
                >Target languages must not include the source language.</span
              >
            }
          </div>
        </div>
      </section>

      <hr class="section-divider" />
      <section class="form-section" id="upload-files">
        <h3 class="section-title">Upload Files for Translation</h3>
        <p class="section-description">
          Select and upload the documents you need to translate.
          <a
            href="#"
            class="help-link"
            (click)="openDialog($event, FiletypesGuideDialogComponent, '622px')"
            >Guide to file types and translation assets.</a
          >
        </p>

        <div class="two-col-grid">
          <!-- Basic Formatting Upload Zone -->
          <div class="upload-section">
            <div
              class="upload-zone"
              [class.drag-over]="basicDragOver()"
              (dragover)="onDragOver($event, 'basic')"
              (dragleave)="onDragLeave($event, 'basic')"
              (drop)="onDrop($event, 'basic')"
              (click)="basicFileInput.click()"
              role="button"
              tabindex="0"
              aria-label="Drag and drop files for basic formatting or click to choose files"
              (keydown.enter)="basicFileInput.click()"
            >
              <span
                class="material-symbols-outlined upload-icon"
                aria-hidden="true"
                >upload_file</span
              >
              <p class="upload-text">
                Drag and drop files here or
                <button
                  type="button"
                  class="choose-files-btn"
                  (click)="$event.stopPropagation(); basicFileInput.click()"
                  (blur)="basicFilesTouched.set(true)"
                >
                  Choose Files
                </button>
              </p>
              <input
                #basicFileInput
                type="file"
                multiple
                class="file-input-hidden"
                (change)="onFileSelected($event, 'basic')"
                aria-hidden="true"
                tabindex="-1"
              />
            </div>
            <p class="upload-hint">
              Up to {{ MAX_FILES_PER_ZONE }} documents of 10MB each.
            </p>
            @if ((submitted() || basicFilesTouched()) && missingBasicFiles()) {
              <span class="field-error" role="alert"
                >At least one file is required.</span
              >
            }

            @if (basicFiles().length > 0) {
              <ul
                class="file-list"
                aria-label="Uploaded basic formatting files"
              >
                @for (file of basicFiles(); track file.name) {
                  <li
                    class="file-item"
                    [class.file-error]="file.status === 'error'"
                  >
                    <div class="file-info">
                      <span
                        class="material-symbols-outlined file-icon"
                        aria-hidden="true"
                        >description</span
                      >
                      <div class="file-data-wrapper">
                        <span class="file-name">{{ file.name }}</span>
                        <span class="file-size">{{ file.sizeFormatted }}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      class="remove-file-btn"
                      (click)="removeFile('basic', $index)"
                      [attr.aria-label]="'Remove ' + file.name"
                    >
                      <span class="material-symbols-outlined" aria-hidden="true"
                        >close</span
                      >
                    </button>
                    @if (file.errorMessage) {
                      <span class="file-error-msg" role="alert">{{
                        file.errorMessage
                      }}</span>
                    }
                  </li>
                }
              </ul>
            }
          </div>

          <!-- DTP explanation section -->
          <div class="dtp-explanation">
            <div class="header">
              <mat-icon>info_outline</mat-icon>
              <div class="dtp-text">
                <p>
                  Some files need extra formatting and layout work during
                  translation.<br />
                  This is called <b>Desktop Publishing</b> (DTP).
                </p>
                <p>
                  Vendors may include additional file preparation costs in the
                  quote.
                </p>
              </div>
            </div>

            <div class="dtp-includes">
              <p>DTP includes:</p>
              <ul>
                <li>PDF Files</li>
                <li>
                  Text inside images, graphics, screenshots that need
                  translation
                </li>
                <li>Charts, diagrams or visuals that need to be recreated</li>
                <li>Tables, forms, or multi-column layouts</li>
                <li>
                  Special formatting: callouts, text over images, custom fonts
                </li>
                <li>Right-to-left or non-Latin script handling</li>
              </ul>
            </div>

            <div class="dtp-question-wrapper">
              <b class="dtp-question"
                >Based on the information above, do your files require Desktop
                Publishing (DTP)?*</b
              >
              <mat-radio-group
                class="radio-group-vertical"
                [value]="requiresDtp()"
                (change)="requiresDtp.set($event.value)"
              >
                <mat-radio-button [value]="true">Yes</mat-radio-button>
                <mat-radio-button [value]="false"
                  >No, my translation is text only and does not require extra
                  formatting</mat-radio-button
                >
              </mat-radio-group>
            </div>
          </div>
        </div>
      </section>

      @if (requiresDtp()) {
        <hr class="section-divider" />

        <section>
          <h3 class="section-title desktop-publishing-title">
            Desktop Publishing (required for files with formatting or visual
            elements)
          </h3>

          <div class="two-col-grid">
            <!-- DTP Work Types -->
            <fieldset class="form-group checkbox-fieldset">
              <legend class="form-label dtp-label">
                Which types of formatting or layout work are needed?<span
                  class="required"
                  aria-hidden="true"
                  >*</span
                >
              </legend>
              <div
                class="checkbox-group-vertical"
                (focusout)="onGroupFocusOut($event, 'dtpWorkTypes')"
              >
                @for (option of dtpWorkTypeOptions; track option) {
                  <mat-checkbox
                    [checked]="isDtpWorkTypeSelected(option)"
                    (change)="toggleDtpWorkType(option, $event.checked)"
                  >
                    {{ option }}
                  </mat-checkbox>
                }
              </div>
              @if (
                quoteForm.get("dtpWorkTypes")?.touched &&
                quoteForm.get("dtpWorkTypes")?.hasError("required")
              ) {
                <span class="field-error" role="alert"
                  >At least one option is required.</span
                >
              }
            </fieldset>

            <!-- DTP Files Selection -->
            <fieldset class="form-group checkbox-fieldset">
              <legend class="form-label dtp-label">
                Which files require DTP?<span
                  class="required"
                  aria-hidden="true"
                  >*</span
                >
              </legend>
              @if (basicFiles().length === 0) {
                <p class="upload-hint">Please upload translation files.</p>
              } @else {
                <div
                  class="checkbox-group-vertical"
                  (focusout)="onGroupFocusOut($event, 'dtpFiles')"
                >
                  @for (file of basicFiles(); track file.name) {
                    @if (file.status === "ready") {
                      <mat-checkbox
                        [checked]="isDtpFileSelected(file.name)"
                        (change)="toggleDtpFile(file.name, $event.checked)"
                      >
                        {{ file.name }}
                      </mat-checkbox>
                    }
                  }
                </div>
                @if (
                  quoteForm.get("dtpFiles")?.touched &&
                  quoteForm.get("dtpFiles")?.hasError("required")
                ) {
                  <span class="field-error" role="alert"
                    >At least one file must be selected.</span
                  >
                }
              }
            </fieldset>
          </div>
        </section>
      }

      <hr class="section-divider" />
      <section class="form-section" id="reference-files">
        <h3 class="section-title">Upload Reference Files (Optional)</h3>
        <p class="section-description">
          Include reference files to guide the vendor.
          <a
            href="#"
            class="help-link"
            (click)="openDialog($event, ReferenceFilesDialogComponent, '622px')"
            >What are reference files?</a
          >
        </p>

        <div class="two-col-grid">
          <div class="upload-section">
            <p class="upload-label">Upload <strong>reference files</strong></p>

            <div
              class="upload-zone"
              [class.drag-over]="referenceDragOver()"
              (dragover)="onDragOver($event, 'reference')"
              (dragleave)="onDragLeave($event, 'reference')"
              (drop)="onDrop($event, 'reference')"
              (click)="referenceFileInput.click()"
              role="button"
              tabindex="0"
              aria-label="Drag and drop reference files or click to choose files"
              (keydown.enter)="referenceFileInput.click()"
            >
              <span
                class="material-symbols-outlined upload-icon"
                aria-hidden="true"
                >upload_file</span
              >
              <p class="upload-text">
                Drag and drop files here or
                <button
                  type="button"
                  class="choose-files-btn"
                  (click)="$event.stopPropagation(); referenceFileInput.click()"
                >
                  Choose Files
                </button>
              </p>
              <input
                #referenceFileInput
                type="file"
                multiple
                class="file-input-hidden"
                (change)="onFileSelected($event, 'reference')"
                aria-hidden="true"
                tabindex="-1"
              />
            </div>
            <p class="upload-hint">
              Up to {{ MAX_FILES_PER_ZONE }} documents of 10MB or less.
            </p>

            @if (referenceFiles().length > 0) {
              <ul class="file-list" aria-label="Uploaded reference files">
                @for (file of referenceFiles(); track file.name) {
                  <li
                    class="file-item"
                    [class.file-error]="file.status === 'error'"
                  >
                    <div class="file-info">
                      <span
                        class="material-symbols-outlined file-icon"
                        aria-hidden="true"
                        >description</span
                      >
                      <div class="file-data-wrapper">
                        <span class="file-name">{{ file.name }}</span>
                        <span class="file-size">{{ file.sizeFormatted }}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      class="remove-file-btn"
                      (click)="removeFile('reference', $index)"
                      [attr.aria-label]="'Remove ' + file.name"
                    >
                      <span class="material-symbols-outlined" aria-hidden="true"
                        >close</span
                      >
                    </button>
                    @if (file.errorMessage) {
                      <span class="file-error-msg" role="alert">{{
                        file.errorMessage
                      }}</span>
                    }
                  </li>
                }
              </ul>
            }
          </div>
        </div>
      </section>

      <hr class="section-divider" />

      <section class="form-section" id="delivery-cost">
        <h3 class="section-title">Delivery and Cost Details</h3>
        <p class="section-description">
          Core details used to uniquely identify each legal entity.
        </p>

        <div class="form-grid three-col">
          <!-- Date Translation Required -->
          <div class="form-group">
            <label for="dateTranslationRequired" class="form-label">
              Date Translation Required:<span
                class="required"
                aria-hidden="true"
                >*</span
              >
            </label>
            <app-datepicker-input
              formControlName="dateTranslationRequired"
              fieldId="dateTranslationRequired"
              placeholder="Select date"
              [minDate]="minDate"
              [dateFilter]="dateFilter"
              [hasError]="isFieldInvalid('dateTranslationRequired')"
              [ariaRequired]="true"
              [ariaInvalid]="isFieldInvalid('dateTranslationRequired')"
            ></app-datepicker-input>
            @if (
              isFieldInvalid("dateTranslationRequired") &&
              quoteForm.get("dateTranslationRequired")?.hasError("required")
            ) {
              <span class="field-error" role="alert"
                >Date Translation Required is required.</span
              >
            }
          </div>

          <!-- Translation Certificate -->
          <div class="form-group">
            <label class="form-label" id="translationCertificate-label">
              Translation Certificate<span class="required" aria-hidden="true"
                >*</span
              >
            </label>
            <mat-radio-group
              id="translationCertificate"
              formControlName="translationCertificate"
              class="radio-group-vertical"
              [class.error]="isFieldInvalid('translationCertificate')"
              aria-labelledby="translationCertificate-label"
            >
              <mat-radio-button value="yes">Yes</mat-radio-button>
              <mat-radio-button value="no">No</mat-radio-button>
            </mat-radio-group>
            @if (
              isFieldInvalid("translationCertificate") &&
              quoteForm.get("translationCertificate")?.hasError("required")
            ) {
              <span class="field-error" role="alert"
                >Selection is required.</span
              >
            }
          </div>

          <!-- Certificate Email (conditional, shown when certificate = yes) -->
          @if (showCertificateEmailField()) {
            <div class="form-group">
              <label class="form-label"> Email to send certificate: </label>
              <div class="email-split-input">
                <input
                  id="certificateEmailUser"
                  type="text"
                  class="form-control email-user-input"
                  formControlName="certificateEmailUser"
                  [class.error]="isFieldInvalid('certificateEmailUser')"
                  aria-required="true"
                  [attr.aria-invalid]="isFieldInvalid('certificateEmailUser')"
                />
                <select
                  id="certificateEmailDomain"
                  class="form-select email-domain-select"
                  formControlName="certificateEmailDomain"
                  [class.error]="isFieldInvalid('certificateEmailDomain')"
                  aria-required="true"
                  [attr.aria-invalid]="isFieldInvalid('certificateEmailDomain')"
                >
                  <option value="@astrazeneca.com">&#64;astrazeneca.com</option>
                  <option value="@astrazeneca.net">&#64;astrazeneca.net</option>
                  <option value="@alexion.com">&#64;alexion.com</option>
                </select>
              </div>
              @if (
                isFieldInvalid("certificateEmailUser") &&
                quoteForm.get("certificateEmailUser")?.hasError("required")
              ) {
                <span class="field-error" role="alert"
                  >Email address is required when certificate is needed.</span
                >
              }
              @if (
                isFieldInvalid("certificateEmailUser") &&
                quoteForm.get("certificateEmailUser")?.hasError("pattern")
              ) {
                <span class="field-error" role="alert"
                  >Please enter a valid email username.</span
                >
              }
            </div>
          }
        </div>
      </section>

      <hr class="section-divider" />

      <!-- ═══════════════════════════════════════════════════════════════════ -->
      <!-- Section 6: Additional Information for Vendors (Optional)          -->
      <!-- ═══════════════════════════════════════════════════════════════════ -->
      <section class="form-section" id="additional-info">
        <h3 class="section-title">
          Additional Information for Vendors (Optional)
        </h3>
        <p class="section-description">
          Share context that affects quality, scope, or schedule.
          <a
            href="#"
            class="help-link"
            (click)="openDialog($event, HelpInfoDialogComponent, '622px')"
            >What's helpful information to include?</a
          >
        </p>

        <div class="form-group">
          <label for="additionalInformation" class="form-label"
            >Additional Information</label
          >
          <textarea
            id="additionalInformation"
            class="form-control form-textarea"
            formControlName="additionalInformation"
            maxlength="1000"
            rows="6"
            aria-describedby="char-counter"
          ></textarea>
          <span class="char-counter" id="char-counter" aria-live="polite">
            {{ additionalInfoCharsRemaining() }} characters remaining
          </span>
        </div>
      </section>

      <!-- ── Submit ──────────────────────────────────────────────────────── -->
      <div class="form-actions">
        <button
          type="submit"
          class="btn btn-primary submit-btn"
          [disabled]="isSubmitting() || formInvalid()"
          aria-label="Submit Quote Request"
        >
          @if (isSubmitting()) {
            Submitting...
          } @else {
            Submit Quote Request
          }
        </button>
      </div>
    </form>
  </div>
</div>
